import { buildRoleSet } from '../../reducers/users/roles';
import {
  ActionDescription,
  AnalyticsProvider,
  ViewDescription,
  ViewMatcher
} from './analytics-types';
import { _noOpProvider } from './base-provider';

let dataDogUserId = '';

const DD_SESSION_KEYS = {
  USER: '_dd_user',
  USER_ATTRS: '_dd_user_attrs'
};

type DDRUM = {
  startView: (view: string | ViewDescription) => void;
  setUser: (user: { name: string; id: number | null; email: string }) => void;
  setUserProperty: (key: string, value: any) => void;
  reset: () => void;
  addAction: (action: ActionDescription, obj: any) => void;
};

const stubbedRUM: DDRUM = {
  startView: () => {
    console.warn('DataDog Rum not defined: startView not initialized');
  },
  setUser: () => {
    console.warn('DataDog Rum not defined: setUser not initialized');
  },
  setUserProperty: () => {
    console.warn('DataDog Rum not defined: setUserProperty not initialized');
  },
  reset: () => {
    console.warn('DataDog Rum not defined: reset not initialized');
  },
  addAction: () => {
    console.warn('DataDog Rum not defined: addAction not initialized');
  }
};

export const getRUM = (): DDRUM => {
  return ((window as any).DD_RUM as DDRUM) || stubbedRUM;
};

export const dataDogFactory = (
  dataDogViewMatchers: ViewMatcher[]
): AnalyticsProvider => {
  const getDataDogViewDescription = (
    url: string
  ): ViewDescription | string | null => {
    for (const { pattern, getDescription } of dataDogViewMatchers) {
      const matches = pattern.exec(url);
      if (matches) {
        return getDescription(matches);
      }
    }
    return null;
  };

  /**
   * Sets the given properties on the RUM session.
   * @param entries
   */
  const setRUMUserProps = (entries: Record<string, any>) => {
    Object.entries(entries).forEach(([key, value]) => {
      getRUM().setUserProperty(key, value);
    });
  };

  /**
   * Pickles the given entries into session storage under the given key.
   * Merges new entries with existing entries.
   * @param key
   * @param entries
   */
  const pickleSessionProps = (key: string, entries: Record<string, any>) => {
    const raw = sessionStorage.getItem(key) ?? '{}';
    let existing: Record<string, any> = {};
    try {
      existing = JSON.parse(raw);
    } catch {
      // corrupted session data — start fresh for this key
    }
    const merged = { ...existing, ...entries };
    sessionStorage.setItem(key, JSON.stringify(merged));
  };

  // hydrate properties if they exist in session storage
  // this is necessary, because there are page reloads
  // _after_ the user has switched contexts
  const initialUserInfo = sessionStorage.getItem(DD_SESSION_KEYS.USER);
  if (!initialUserInfo) {
    // just to make sure that we dont have any stale data
    sessionStorage.removeItem(DD_SESSION_KEYS.USER_ATTRS);
  } else {
    try {
      getRUM().setUser(JSON.parse(initialUserInfo));
    } catch {
      sessionStorage.removeItem(DD_SESSION_KEYS.USER);
    }

    const initialUserAttrs = sessionStorage.getItem(DD_SESSION_KEYS.USER_ATTRS);
    if (initialUserAttrs) {
      try {
        const userAttrs = JSON.parse(initialUserAttrs);
        setRUMUserProps(userAttrs);
      } catch (e) {
        // silently fail if the user attributes are not valid JSON
      }
    }
  }

  return {
    ..._noOpProvider,
    handleLocationChange: (location: Location) => {
      const viewDescriptor = getDataDogViewDescription(location.pathname);
      if (viewDescriptor) {
        getRUM().startView(viewDescriptor);
      }
    },
    setContext: (context: CMx.Context) => {
      const activeContext = context.activeContext ?? ({} as any);

      const userAttributes = {
        roles: buildRoleSet(context),
        healthSystem: activeContext.healthSystemCode,
        organization: activeContext.organizationCode,
        organizationType: activeContext.organizationType
      };
      setRUMUserProps(userAttributes);
      pickleSessionProps(DD_SESSION_KEYS.USER_ATTRS, userAttributes);
    },
    setUser: (user: CMxAPI.User) => {
      if (dataDogUserId === user.userId) {
        return;
      }
      dataDogUserId = user.userId;
      const userInfo = {
        id: user.id,
        name: user.firstName + ' ' + user.lastName,
        email: user.username
      };
      getRUM().setUser(userInfo);
      sessionStorage.setItem(DD_SESSION_KEYS.USER, JSON.stringify(userInfo));
    },
    setServiceLine: (serviceLine: CMxAPI.ServiceLine) => {
      const serviceLineInfo = {
        serviceLine: serviceLine?.name ?? '',
        displayServiceLine: serviceLine?.displayName ?? ''
      };
      setRUMUserProps(serviceLineInfo);
      pickleSessionProps(DD_SESSION_KEYS.USER_ATTRS, serviceLineInfo);
    },
    trackView: (viewName: string | ViewDescription) => {
      getRUM().startView(viewName);
    },
    trackAction: (
      action: ActionDescription,
      data?: Record<string, unknown>
    ) => {
      // Implementation for tracking actions
      getRUM().addAction(action, data);
    },
    reset: () => {
      dataDogUserId = '';
    }
  };
};
